import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { usePageTitle } from '../hooks/shared';

function Section({ title, children }) {
  return (
    <section className="space-y-3 border-t border-neutral-200 pt-8 first:border-t-0 first:pt-0">
      <h2 className="text-2xl font-semibold text-neutral-900">{title}</h2>
      <div className="space-y-4 text-sm leading-7 text-neutral-700 md:text-base">{children}</div>
    </section>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default function PrivacyPolicy() {
  usePageTitle('Privacy Policy');

  const effectiveDate = 'April 9, 2026';

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-10 rounded-3xl border border-neutral-200 bg-neutral-50 p-6 md:p-8">
          <p className="mb-3 text-sm font-medium uppercase tracking-[0.2em] text-neutral-500">
            Privacy Policy
          </p>
          <h1 className="mb-4 text-4xl font-bold tracking-tight text-neutral-900">
            VotePlan Privacy Policy
          </h1>
          <p className="max-w-3xl text-base leading-7 text-neutral-700 md:text-lg">
            VotePlan helps people find elections, understand deadlines, manage ballot-guide delivery
            preferences, and receive voting-related reminders. This Privacy Policy explains what
            information we collect, how we use it, when we share it, and the choices available to
            users.
          </p>
          <p className="mt-4 text-sm text-neutral-500">Effective date: {effectiveDate}</p>
        </div>

        <div className="space-y-8">
          <Section title="1. Scope">
            <p>
              This Privacy Policy applies to VotePlan websites, mobile-friendly web experiences,
              user accounts, ballot-guide tools, address lookups, reminder features, and related
              services we operate. It does not apply to third-party websites, government election
              authority sites, payment processors, mapping providers, or other external services
              that may be linked from our app.
            </p>
          </Section>

          <Section title="2. Information we collect">
            <p>We may collect the following categories of information:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Account information</strong>, such as name, email address, password hash,
                and authentication details.
              </li>
              <li>
                <strong>Address and location-related information</strong>, such as saved mailing
                addresses, entered addresses, geocoding results, and district or jurisdiction
                matches used to show relevant election information.
              </li>
              <li>
                <strong>Delivery and subscription information</strong>, such as ballot-guide
                delivery preferences, mailing name, mailing address, reminder settings, and delivery
                history.
              </li>
              <li>
                <strong>Order and transaction information</strong>, such as mailer orders,
                payment-related metadata, fulfillment status, and limited records needed to manage
                billing or delivery. We do not store full payment card numbers ourselves.
              </li>
              <li>
                <strong>Communications information</strong>, such as messages you send to us,
                support requests, and responses to administrative or service notices.
              </li>
              <li>
                <strong>Usage and device information</strong>, such as browser type, IP address,
                pages viewed, referring URLs, approximate location derived from IP, timestamps, and
                basic analytics or diagnostic data.
              </li>
              <li>
                <strong>Public civic and election data</strong>, such as election schedules,
                deadlines, and authority information obtained from public sources, which may be
                associated with a state, county, or jurisdiction rather than a specific person.
              </li>
            </ul>
          </Section>

          <Section title="3. How we collect information">
            <ul className="list-disc space-y-2 pl-6">
              <li>
                Directly from you when you create an account, enter an address, or place an order.
              </li>
              <li>
                Automatically when you use the service, including through cookies, logs, and
                analytics tools.
              </li>
              <li>
                From service providers that help us process payments, send email, print and mail
                materials, perform geocoding, or host the application.
              </li>
              <li>
                From public election authorities, government agencies, and other public sources that
                publish election data.
              </li>
            </ul>
          </Section>

          <Section title="4. How we use information">
            <p>We use personal information to:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Provide, maintain, and improve the service.</li>
              <li>Create and manage user accounts.</li>
              <li>Match addresses to relevant elections, districts, and voting information.</li>
              <li>Generate ballot guides, reminders, and delivery workflows you request.</li>
              <li>Process purchases, coordinate fulfillment, and maintain delivery records.</li>
              <li>Send transactional messages, service notices, and support responses.</li>
              <li>Protect the service, detect fraud or abuse, and troubleshoot issues.</li>
              <li>Comply with legal obligations and enforce our terms.</li>
              <li>
                Analyze usage trends and product performance so we can improve reliability,
                usability, and accessibility.
              </li>
            </ul>
          </Section>

          <Section title="5. Legal bases for processing">
            <p>
              Where applicable under law, we process personal information because it is necessary to
              perform a contract with you, because we have legitimate interests in operating and
              securing the service, because you consented to certain processing, or because we must
              comply with legal obligations.
            </p>
          </Section>

          <Section title="6. When we share information">
            <p>
              We do not sell personal information for money. We may share information in these
              situations:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Service providers.</strong> We use vendors that help with hosting,
                authentication, analytics, geocoding, payments, email delivery, print and mail
                fulfillment, customer support, and infrastructure operations.
              </li>
              <li>
                <strong>Election or map integrations.</strong> When you open links or use features
                that depend on external civic or mapping services, relevant request data may be sent
                to those services.
              </li>
              <li>
                <strong>Legal and safety reasons.</strong> We may disclose information if required
                by law or if reasonably necessary to protect users, the public, or the service.
              </li>
              <li>
                <strong>Business transfers.</strong> Information may be transferred as part of a
                merger, financing, acquisition, reorganization, or sale of assets.
              </li>
              <li>
                <strong>With your direction.</strong> We may share information when you ask us to do
                so or intentionally use a feature that involves a third party.
              </li>
            </ul>
          </Section>

          <Section title="7. Cookies and similar technologies">
            <p>
              We may use cookies, local storage, pixels, and similar technologies to keep you signed
              in, remember settings, understand traffic patterns, measure feature usage, and improve
              performance. You can control cookies through your browser settings, but some site
              functions may not work properly if certain cookies are disabled.
            </p>
          </Section>

          <Section title="8. Data retention">
            <p>
              We keep information for as long as reasonably necessary to provide the service,
              maintain user accounts, complete transactions, meet legal requirements, resolve
              disputes, and enforce agreements. Retention periods vary by data type. For example,
              account and delivery records may be kept longer than transient log data. We may also
              retain de-identified or aggregated information for analytics, reporting, and product
              improvement.
            </p>
          </Section>

          <Section title="9. Data security">
            <p>
              We use reasonable administrative, technical, and organizational safeguards designed to
              protect personal information. These may include access controls, encrypted transport,
              credential protections, and vendor security measures. No method of storage or
              transmission is completely secure, and we cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="10. Your choices and rights">
            <p>Depending on where you live, you may have rights to:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Access the personal information we hold about you.</li>
              <li>Correct inaccurate or incomplete information.</li>
              <li>Delete certain information.</li>
              <li>Object to or restrict certain processing.</li>
              <li>Withdraw consent where processing is based on consent.</li>
              <li>Request a copy of certain information in a portable format.</li>
              <li>Opt out of marketing communications.</li>
            </ul>
            <p>
              You may also update some information directly through your account settings. To make a
              privacy request, contact us using the information below. We may need to verify your
              identity before completing a request.
            </p>
          </Section>

          <Section title="11. Children’s privacy">
            <p>
              VotePlan is intended for a general audience and is not directed to children under 13.
              We do not knowingly collect personal information from children under 13. If you
              believe a child has provided personal information to us, contact us and we will take
              appropriate steps.
            </p>
          </Section>

          <Section title="12. Third-party links and services">
            <p>
              The service may link to third-party websites, including election authorities,
              government registries, payment processors, or mapping tools. Their privacy practices
              are governed by their own policies, not this one. Review those policies before
              providing information directly to them.
            </p>
          </Section>

          <Section title="13. State privacy disclosures">
            <p>
              Residents of certain U.S. states may have additional privacy rights under applicable
              law. We honor requests as required by those laws and do not discriminate against users
              for exercising privacy rights. If required for your jurisdiction, you may designate an
              authorized agent to make a request on your behalf, subject to identity verification
              and proof of authority.
            </p>
          </Section>

          <Section title="14. International users">
            <p>
              VotePlan is based in the United States and is intended primarily for U.S. voters and
              election-related use cases. If you access the service from outside the United States,
              your information may be transferred to and processed in the United States or other
              jurisdictions where our service providers operate.
            </p>
          </Section>

          <Section title="15. Changes to this Privacy Policy">
            <p>
              We may update this Privacy Policy from time to time. If we make material changes, we
              will post the updated policy here and revise the effective date above. Your continued
              use of the service after the updated policy becomes effective means the updated policy
              will apply.
            </p>
          </Section>

          <Section title="16. Contact us">
            <p>
              For privacy questions, requests, or concerns, contact us at{' '}
              <a
                className="font-medium text-primary-600 hover:text-primary-700"
                href="mailto:privacy@voteplan.app"
              >
                privacy@voteplan.app
              </a>
              .
            </p>
            <p>
              You may also contact us through the main support channels listed elsewhere in the
              application.
            </p>
          </Section>
        </div>

        <div className="mt-12 rounded-2xl border border-neutral-200 bg-neutral-50 p-5 text-sm text-neutral-600">
          <p>
            This page is provided for general informational purposes and should be reviewed by a
            licensed attorney before production launch, especially if you will collect sensitive
            personal data, use ad-tech cookies, support SMS campaigns, or operate in states with
            additional privacy disclosure requirements.
          </p>
          <p className="mt-3">
            Related pages:{' '}
            <Link className="font-medium text-primary-600 hover:text-primary-700" to="/terms">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link className="font-medium text-primary-600 hover:text-primary-700" to="/settings">
              account settings
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
